import React, { useState, useEffect, useRef } from 'react';
import { useJWTAuth } from '../../auth/JWTAuthContext';
import { useJWTSync } from '../../auth/useJWTSync';
import { socketService } from '../../services/SocketService';
import { useToast } from '../../contexts/ToastContext';

const AttributePanel = ({ hero, onAttributeUpdate }) => {
  const { user, getAuthHeaders, isAuthenticated } = useJWTAuth();
  const { jwtUser } = useJWTSync(); // Sync JWT with entitlements
  const { error } = useToast();
  const [loading, setLoading] = useState({});
  const [costs, setCosts] = useState({});
  const [calculatingCosts, setCalculatingCosts] = useState(true);
  const [costsFetched, setCostsFetched] = useState(false);
  const [showSpentPoints, setShowSpentPoints] = useState(false);
  
  // Preload success sound effect once
  const successSoundRef = useRef(null);
  
  const isOwner = isAuthenticated() && user && hero.owner_id === user.id;
  
  // Debug logging
  /*console.log('🔍 [AttributePanel] Owner check:', {
    isAuthenticated: isAuthenticated(),
    user: user,
    userId: user?.id,
    warriorOwnerId: hero.owner_id,
    isOwner: isOwner
  });*/

  const primaryAttributes = [
    { key: 'strength', name: 'Strength', color: 'green', description: 'Affects melee damage and health' },
    { key: 'endurance', name: 'Endurance', color: 'green', description: 'Affects health and stamina' },
    { key: 'coordination', name: 'Coordination', color: 'green', description: 'Affects finesse weapons and defense' },
    { key: 'quickness', name: 'Quickness', color: 'green', description: 'Affects move speed and attack speed' },
    { key: 'focus', name: 'Focus', color: 'green', description: 'Affects mana and magic skills' },
    { key: 'self', name: 'Self', color: 'green', description: 'Affects mana and magic defense' }
  ];

  const secondaryAttributes = [
    { key: 'health', name: 'Health', color: 'green' },
    { key: 'stamina', name: 'Stamina', color: 'green' },
    { key: 'mana', name: 'Mana', color: 'green' }
  ];

  const getColorClass = (color) => {
    const colors = {
      red: 'text-red-400',
      green: 'text-green-400',
      blue: 'text-blue-400',
      purple: 'text-purple-400',
      yellow: 'text-yellow-400',
      cyan: 'text-cyan-400'
    };
    return colors[color] || 'text-gray-400';
  };

  const getBorderColorClass = (color) => {
    const colors = {
      red: 'border-red-500',
      green: 'border-green-500',
      blue: 'border-blue-500',
      purple: 'border-purple-500',
      yellow: 'border-yellow-500',
      cyan: 'border-cyan-500'
    };
    return colors[color] || 'border-gray-500';
  };

  // Play success sound effect (reuses preloaded audio)
  const playSuccessSound = () => {
    try {
      if (successSoundRef.current) {
        successSoundRef.current.currentTime = 0; // Reset to start
        successSoundRef.current.play().catch(e => console.log('Could not play success sound:', e));
      }
    } catch (error) {
      console.log('Error playing success sound:', error);
    }
  };

  const fetchAttributeCost = async (attributeName) => {
    try {
      const response = await fetch(`/api/heroes/${hero.id}/attribute-cost/${attributeName}`);
      const data = await response.json();

      if (data.success) {
        setCosts(prev => ({
          ...prev,
          [attributeName]: data.data.cost
        }));
      }
    } catch (error) {
      console.error('Error fetching attribute cost:', error);
    }
  };

  const fetchAllAttributeCosts = async () => {
    try {
      console.log('fetching all attribute costs for hero', hero.id);
      const response = await fetch(`/api/heroes/${hero.id}/attribute-costs`);
      const data = await response.json();

      if (data.success) {
        setCosts(data.data.costs);
      }
    } catch (error) {
      console.error('Error fetching all attribute costs:', error);
    }
  };

  const spendXP = (attributeName, isSecondary = false) => {
    if (loading[attributeName]) {
      console.log('Attribute already loading, skipping spendXP');
      return;
    }
    console.log('spendXP: setting loading state for attribute', attributeName, 'to true');
    
    setLoading(prev => ({ ...prev, [attributeName]: true }));
    
    // Use socket service for real-time XP spending
    if (socketService.isSocketConnected()) {

      console.log('spending XP via socket for attribute', attributeName);
      const data = {
        heroId: hero.id
      };
      
      if (isSecondary) {
        data.secondaryName = attributeName;
        socketService.spendXPSecondary(data);
      } else {
        data.attributeName = attributeName;
        socketService.spendXP(data);
      }
    } else {
      // Fallback to HTTP if socket not connected
      console.warn('Socket not connected, falling back to HTTP');
      spendXPHTTP(attributeName, isSecondary);
    }
  };

  // Fallback HTTP method
  const spendXPHTTP = async (attributeName, isSecondary = false) => {
    try {
      const endpoint = isSecondary 
        ? `/api/heroes/${hero.id}/spend-xp-secondary`
        : `/api/heroes/${hero.id}/spend-xp`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          [isSecondary ? 'secondaryName' : 'attributeName']: attributeName
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Update the hero data
        onAttributeUpdate(data.data.hero);
        
        // Play success sound effect
        playSuccessSound();
        
        // Refresh all attribute costs since the hero data has been updated
        await fetchAllAttributeCosts();
        
        // Clear all loading states after costs are refreshed
        setLoading({});
      } else {
        console.error('Failed to spend XP:', data.error);
        error(`Failed to spend XP: ${data.error}`);
        // Clear only this attribute's loading state on error
        setLoading(prev => ({ ...prev, [attributeName]: false }));
      }
    } catch (error) {
      console.error('Error spending XP:', error);
      error('Error spending XP');
      // Clear only this attribute's loading state on error
      setLoading(prev => ({ ...prev, [attributeName]: false }));
    }
  };

  // Preload success sound on mount
  useEffect(() => {
    successSoundRef.current = new Audio('/assets/sfx/ui/bell-ding-586.ogg');
    successSoundRef.current.volume = 0.7;
    successSoundRef.current.load(); // Preload the audio
    
    return () => {
      // Clean up audio on unmount
      if (successSoundRef.current) {
        successSoundRef.current.pause();
        successSoundRef.current = null;
      }
    };
  }, []); // Only run once on mount

  // Set up socket event listeners ONCE on mount (separate from cost fetching)
  useEffect(() => {
    console.log('📡 Setting up socket event listeners');
    
    // Set up socket event listeners for real-time XP spending
    socketService.onXPSpent((data) => {
      if (data.success) {
        // Update hero data
        onAttributeUpdate(data.hero);
        
        // Update costs from response - no separate API call needed
        setCosts(data.costs);
        
        // Play success sound effect
        playSuccessSound();
        
        console.log('✅ XP spent successfully via socket:', data);
      }
      // Clear loading state for all attributes
      setLoading({});
    });

    socketService.onXPError((data) => {
      console.error('❌ XP spending error via socket:', data);
      error(`Failed to spend XP: ${data.error}`);
      // Clear loading state for all attributes
      setLoading({});
    });

    // Set up socket event listeners for attribute costs
    socketService.onAttributeCosts((data) => {
      if (data.success) {
        console.log('📊 Received attribute costs via socket:', data.costs);
        setCosts(data.costs);
        setCalculatingCosts(false);
      }
    });

    socketService.onAttributeCostsError((data) => {
      console.error('❌ Attribute costs error via socket:', data);
      // Fallback to HTTP if socket fails
      console.log('📊 Falling back to HTTP for attribute costs');
      fetchAllAttributeCosts().finally(() => {
        setCalculatingCosts(false);
      });
    });

    // Cleanup listeners only on unmount (when hero.id changes)
    return () => {
      console.log('🧹 Cleaning up socket listeners for hero', hero.id);
      socketService.removeListeners(['xp-spent', 'xp-error', 'attribute-costs', 'attribute-costs-error']);
    };
  }, [hero.id]); // Only re-run when hero changes

  // Fetch attribute costs (separate effect)
  useEffect(() => {
    // Only fetch costs once per hero
    if (costsFetched) {
      setCalculatingCosts(false);
      console.log('📊 Costs already fetched for hero', hero.id);
      return;
    }

    // Fetch costs for all attributes - try socket first, fallback to HTTP
    console.log('📊 Fetching all attribute costs for hero', hero.id);
    setCalculatingCosts(true);
    setCostsFetched(true); // Mark as fetched to prevent duplicate calls
    
    if (socketService.isSocketConnected()) {
      // Use socket for real-time cost fetching
      console.log('📊 Fetching attribute costs via socket');
      socketService.getAttributeCosts({ heroId: hero.id });
    } else {
      // Fallback to HTTP
      console.log('📊 Socket not connected, using HTTP fallback for attribute costs');
      fetchAllAttributeCosts().finally(() => {
        setCalculatingCosts(false);
      });
    }
  }, [hero.id, costsFetched]);

  // Reset costsFetched when hero changes
  useEffect(() => {
    setCostsFetched(false);
    setCosts({});
    setCalculatingCosts(true);
  }, [hero.id]);

  return (
    <div className="bg-gray-800 rounded-lg p-2 max-w-full md:max-w-[600px]">
      <div className="flex items-center justify-between mb-1">
        <button
          onClick={() => setShowSpentPoints(!showSpentPoints)}
          className="flex items-center gap-2 px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white transition-colors text-sm"
          title="Toggle spent points display"
        >
          <span>{showSpentPoints ? '👁️' : '👁️‍🗨️'}</span>
          <span>{showSpentPoints ? 'Hide' : 'Show'} Spent Points</span>
        </button>
        <p className="text-yellow-400 font-bold text-md">
          Unspent XP: {hero.unspent_xp.toLocaleString()}
        </p>
      </div>

      {/* Primary Attributes */}
      <div className="mb-6">
        <h4 className="text-lg font-semibold text-white mb-3">Primary Attributes</h4>
        <div className="space-y-1">
          {primaryAttributes.map(attr => {
          const currentValue = hero[attr.key];
          const spentPoints = hero[`primary_spent_${attr.key}`] || 0;
          const cost = costs[attr.key] || 0;
          const canAfford = hero.unspent_xp >= cost;
          const isMaxLevel = currentValue >= 200;
          const isLoading = loading[attr.key];

          return (
            <div 
              key={attr.key}
              className={`flex items-center justify-between py-2 px-3 rounded border-l-4 transition-all duration-200 ${
                getBorderColorClass(attr.color)
              }`}
            >
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h4 className={`font-semibold ${getColorClass(attr.color)}`}>
                    {attr.name}
                  </h4>
                  <span className="text-white font-bold">
                    {currentValue}
                    {showSpentPoints && (
                      <span className="text-gray-400 text-sm ml-1">
                        ({spentPoints} spent)
                      </span>
                    )}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                {!isMaxLevel && (
                  <div className="text-right">
                    <div className="text-gray-400 text-xs">Cost:</div>
                    <div className={`font-bold text-sm ${canAfford ? 'text-yellow-400' : 'text-red-400'}`}>
                      {calculatingCosts ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-yellow-400 mr-1"></div>
                          ...
                        </div>
                      ) : (
                        `${cost.toLocaleString()} XP`
                      )}
                    </div>
                  </div>
                )}
                
                {isOwner && (
                  isMaxLevel ? (
                    <div className="px-1 py-1 rounded text-sm font-bold text-yellow-400">
                      MAX
                    </div>
                  ) : (
                    <button
                      onClick={() => spendXP(attr.key)}
                      disabled={!canAfford || isLoading || calculatingCosts}
                      className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                        canAfford && !isLoading && !calculatingCosts
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {isLoading ? '...' : '+1'}
                    </button>
                  )
                )}
              </div>
            </div>
          );
        })}
        </div>
      </div>

      {/* Secondary Attributes */}
      <div className="mb-6">
        <h4 className="text-lg font-semibold text-white mb-3">Secondary Attributes</h4>
        <div className="space-y-1">
          {secondaryAttributes.map(attr => {
            const currentValue = hero[attr.key] || 0;
            const spentPoints = hero[`secondary_spent_${attr.key}`] || 0;
            const cost = costs[attr.key] || 0;
            const canAfford = hero.unspent_xp >= cost;
            const isLoading = loading[attr.key];
            // Secondary attributes don't have a hard max like primary attributes

            return (
              <div 
                key={attr.key}
                className={`flex items-center justify-between py-2 px-3 rounded border-l-4 transition-all duration-200 ${
                  getBorderColorClass(attr.color)
                }`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h4 className={`font-semibold ${getColorClass(attr.color)}`}>
                      {attr.name}
                    </h4>
                    <span className="text-white font-bold">
                      {currentValue}
                      {showSpentPoints && (
                        <span className="text-gray-400 text-sm ml-1">
                          ({spentPoints} spent)
                        </span>
                      )}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-gray-400 text-xs">Cost:</div>
                    <div className={`font-bold text-sm ${canAfford ? 'text-yellow-400' : 'text-red-400'}`}>
                      {calculatingCosts ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-yellow-400 mr-1"></div>
                          ...
                        </div>
                      ) : (
                        `${cost.toLocaleString()} XP`
                      )}
                    </div>
                  </div>
                  
                  {isOwner && (
                    <button
                      onClick={() => spendXP(attr.key, true)}
                      disabled={!canAfford || isLoading || calculatingCosts}
                      className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                        canAfford && !isLoading && !calculatingCosts
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {isLoading ? '...' : '+1'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-6 p-4 bg-gray-700 rounded-lg">
        <h4 className="text-white font-bold mb-4">Attribute Descriptions</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-3">
            <div>
              <div className="font-semibold text-red-400 mb-1">Strength</div>
              <div className="text-gray-300 text-xs">Increases melee damage and health</div>
            </div>
            <div>
              <div className="font-semibold text-green-400 mb-1">Endurance</div>
              <div className="text-gray-300 text-xs">Boosts health and stamina regeneration</div>
            </div>
            <div>
              <div className="font-semibold text-blue-400 mb-1">Coordination</div>
              <div className="text-gray-300 text-xs">Improves accuracy and critical hit chance</div>
            </div>
            <div>
              <div className="font-semibold text-purple-400 mb-1">Quickness</div>
              <div className="text-gray-300 text-xs">Increases movement speed and attack speed</div>
            </div>
            <div>
              <div className="font-semibold text-yellow-400 mb-1">Focus</div>
              <div className="text-gray-300 text-xs">Enhances mana regeneration and spell power</div>
            </div>
            <div>
              <div className="font-semibold text-cyan-400 mb-1">Self</div>
              <div className="text-gray-300 text-xs">Improves overall resilience and special abilities</div>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <div className="font-semibold text-red-300 mb-1">Health</div>
              <div className="text-gray-300 text-xs">Maximum health points for survival</div>
            </div>
            <div>
              <div className="font-semibold text-green-300 mb-1">Stamina</div>
              <div className="text-gray-300 text-xs">Energy for special moves and sustained combat</div>
            </div>
            <div>
              <div className="font-semibold text-blue-300 mb-1">Mana</div>
              <div className="text-gray-300 text-xs">Magical energy for spells and abilities</div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 p-4 bg-gray-700 rounded-lg">
        <h4 className="text-white font-bold mb-2">Attribute Effects</h4>
        <div className="text-sm text-gray-300 space-y-1">
          <div><span className="text-blue-400">Strength:</span> +{hero.strength} melee damage, +{Math.floor(hero.strength * 0.5)} health</div>
          <div><span className="text-blue-400">Endurance:</span> +{hero.endurance * 2} health, +{Math.floor(hero.endurance * 0.3)} stamina</div>
          <div><span className="text-blue-400">Coordination:</span> +{Math.floor(hero.coordination * 0.8)} defense, +{Math.floor(hero.coordination * 0.5)} ranged damage</div>
          <div><span className="text-blue-400">Quickness:</span> +{Math.floor(hero.quickness * 0.5)} move speed, +{Math.floor(hero.quickness * 0.3)} attack speed</div>
          <div><span className="text-blue-400">Focus:</span> +{hero.focus} mana, +{Math.floor(hero.focus * 0.7)} magic damage</div>
          <div><span className="text-blue-400">Self:</span> +{Math.floor(hero.self * 0.5)} mana, +{Math.floor(hero.self * 0.6)} magic defense</div>
        </div>
      </div>
    </div>
  );
};

export default AttributePanel;
